import React, { useState, useMemo, useRef } from 'react';
import { uid, formatMontant } from '../../utils/helpers';
import { CATEGORIES, MOIS_LABELS, TYPE_CONFIG } from './comptaUtils';

const EMPTY_FORM = { date: '', libelle: '', categorie: 'Divers', debit: '', credit: '', note: '' };

export default function OngletJournal({
  adherents, selectedYear, activeMois, setActiveMois,
  comptabilite, saveComptaOperation, deleteComptaOperation, setComptaMoisData,
}) {
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editId, setEditId]       = useState(null);
  const [soldeInput, setSoldeInput] = useState('');
  const [editSolde, setEditSolde] = useState(false);
  const [pieceJointe, setPieceJointe] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const docRef = useRef();

  const f = k => v => setForm(p => ({ ...p, [k]: v }));

  // Recherche globale tous mois de l'année
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const yearData = comptabilite[selectedYear] || {};
    const results = [];
    for (let m = 1; m <= 12; m++) {
      const ops = yearData[m]?.operations || [];
      ops.forEach(op => {
        const haystack = [
          op.libelleBrut, op.fullLibelle, op.categorie, op.note, op.adherentNom
        ].filter(Boolean).join(' ').toLowerCase();
        if (haystack.includes(q)) results.push({ ...op, _mois: m });
      });
    }
    return results.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [searchQuery, comptabilite, selectedYear]);

  const moisData   = comptabilite[selectedYear]?.[activeMois] || { operations: [], soldeReleve: null, soldeOuverture: null };
  const operations = useMemo(() =>
    [...(moisData.operations || [])].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [moisData.operations]);

  // Running balance
  const withSolde = useMemo(() => {
    let solde = moisData.soldeOuverture || 0;
    return operations.map(op => {
      solde = solde + (op.credit || 0) - (op.debit || 0);
      return { ...op, soldeApres: solde };
    });
  }, [operations, moisData.soldeOuverture]);

  const totalCredit = operations.reduce((s, o) => s + (o.credit || 0), 0);
  const totalDebit  = operations.reduce((s, o) => s + (o.debit  || 0), 0);
  const soldeCalc   = (moisData.soldeOuverture || 0) + totalCredit - totalDebit;
  const soldeReleve = moisData.soldeReleve;
  const ecart       = soldeReleve != null ? (soldeCalc - soldeReleve) : null;

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5 Mo'); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = ev => setPieceJointe({ data: ev.target.result, type: file.type, nom: file.name });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ ...EMPTY_FORM, date: today });
    setPieceJointe(null);
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = op => {
    setForm({ date: op.date || '', libelle: op.libelleBrut || '', categorie: op.categorie || 'Divers',
      debit: op.debit || '', credit: op.credit || '', note: op.note || '' });
    setPieceJointe(op.pieceJointe || null);
    setEditId(op.id);
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.date || !form.libelle) return;
    const debit  = parseFloat(form.debit)  || 0;
    const credit = parseFloat(form.credit) || 0;
    const op = {
      id: editId || uid(),
      date: form.date,
      dateAffichage: form.date.slice(8) + '/' + form.date.slice(5,7),
      libelleBrut: form.libelle,
      fullLibelle: form.libelle,
      type: debit > 0 ? 'E' : 'A',
      categorie: form.categorie,
      debit, credit,
      adherentId: null, matchStatus: 'none', matchScore: 999,
      statut: 'valide', note: form.note,
      pieceJointe: pieceJointe,
      source: 'manuel', pointe: true, validated: true,
    };
    saveComptaOperation(selectedYear, activeMois, op);
    setShowForm(false); setForm(EMPTY_FORM); setEditId(null); setPieceJointe(null);
  };

  const handleDelete = id => {
    deleteComptaOperation(selectedYear, activeMois, id);
    setConfirmDel(null);
  };

  const saveSoldeReleve = () => {
    const v = parseFloat(soldeInput.replace(',', '.')) || 0;
    setComptaMoisData(selectedYear, activeMois, { soldeReleve: v });
    setEditSolde(false);
  };

  const saveSoldeOuverture = () => {
    const prev = comptabilite[selectedYear]?.[activeMois - 1]?.soldeReleve;
    if (prev != null) {
      setComptaMoisData(selectedYear, activeMois, { soldeOuverture: prev });
    }
  };

  return (
    <div>
      {/* Barre de recherche globale */}
      <div style={{ marginBottom:16 }}>
        <div style={{ position:'relative' }}>
          <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:15, pointerEvents:'none' }}>🔍</span>
          <input
            className="form-control"
            style={{ paddingLeft:34, fontSize:13 }}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Rechercher dans tous les mois de l'année (libbellé, nom, catégorie...)"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:16 }}>✕</button>
          )}
        </div>
        {searchResults && (
          <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>
            {searchResults.length} résultat(s) dans {selectedYear}
          </div>
        )}
      </div>

      {/* Résultats de recherche globale */}
      {searchResults && (
        <div className="card mb-4">
          <div className="card-header">
            <div className="card-title">🔍 Résultats — « {searchQuery} »</div>
            <span style={{ fontSize:12, color:'#6b7280' }}>{searchResults.length} opération(s)</span>
          </div>
          {searchResults.length === 0 ? (
            <div className="empty-state" style={{ padding:'24px 0' }}>
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-text">Aucune opération trouvée</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:50 }}>Mois</th>
                    <th style={{ width:75 }}>Date</th>
                    <th>Libellé</th>
                    <th style={{ width:120 }}>Catégorie</th>
                    <th style={{ textAlign:'right', width:85 }}>Débit</th>
                    <th style={{ textAlign:'right', width:85 }}>Crédit</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map(op => {
                    const cfg = TYPE_CONFIG[op.type] || TYPE_CONFIG['E'];
                    return (
                      <tr key={op.id} onClick={() => { setSearchQuery(''); setActiveMois(op._mois); }} style={{ cursor:'pointer' }} title="Cliquer pour ouvrir ce mois">
                        <td style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{MOIS_LABELS[op._mois-1]?.slice(0,3)}</td>
                        <td style={{ fontSize:11, color:'#6b7280' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                        <td>
                          <div style={{ fontSize:12 }}>{op.libelleBrut}</div>
                          {op.note && <div style={{ fontSize:10, color:'#9ca3af' }}>{op.note}</div>}
                          <span style={{ background:cfg.bg, color:cfg.color, borderRadius:999, padding:'1px 6px', fontSize:9, fontWeight:700 }}>{cfg.label}</span>
                        </td>
                        <td style={{ fontSize:11, color:'#6b7280' }}>{op.categorie}</td>
                        <td style={{ textAlign:'right', color:'#991b1b', fontWeight:700, fontSize:12 }}>{op.debit > 0 ? formatMontant(op.debit) : ''}</td>
                        <td style={{ textAlign:'right', color:'#166534', fontWeight:700, fontSize:12 }}>{op.credit > 0 ? formatMontant(op.credit) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Month selector */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16, opacity: searchResults ? 0.5 : 1 }}>
        {MOIS_LABELS.map((m, i) => {
          const hasData = !!(comptabilite[selectedYear]?.[i+1]?.operations?.length);
          return (
            <button key={i} className={`btn btn-sm ${activeMois === i+1 ? 'btn-primary' : 'btn-outline'}`}
              style={{ fontSize:11, position:'relative' }}
              onClick={() => setActiveMois(i+1)}>
              {m.slice(0,3)}
              {hasData && <span style={{ position:'absolute', top:-4, right:-4, width:8, height:8, background:'#166534', borderRadius:'50%' }} />}
            </button>
          );
        })}
        <button className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }} onClick={openCreate}>+ Opération manuelle</button>
      </div>

      {/* KPI band */}
      <div className="grid-3 mb-4" style={{ gap:12 }}>
        <div className="stat-card" style={{ borderTopColor:'#166534' }}>
          <div className="stat-label">Total crédits</div>
          <div className="stat-value" style={{ color:'#166534' }}>{formatMontant(totalCredit)}</div>
        </div>
        <div className="stat-card" style={{ borderTopColor:'#991b1b' }}>
          <div className="stat-label">Total débits</div>
          <div className="stat-value" style={{ color:'#991b1b' }}>{formatMontant(totalDebit)}</div>
        </div>
        <div className="stat-card" style={{ borderTopColor: (totalCredit - totalDebit) >= 0 ? '#166534' : '#991b1b' }}>
          <div className="stat-label">Résultat net</div>
          <div className="stat-value" style={{ color: (totalCredit - totalDebit) >= 0 ? '#166534' : '#991b1b' }}>
            {formatMontant(totalCredit - totalDebit)}
          </div>
        </div>
      </div>

      {/* Journal table */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="card-title">📒 Journal — {MOIS_LABELS[activeMois-1]} {selectedYear}</div>
          <span style={{ fontSize:12, color:'#6b7280' }}>{operations.length} opération(s)</span>
        </div>
        {operations.length === 0 ? (
          <div className="empty-state" style={{ padding:'30px 0' }}>
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">Aucune opération pour ce mois</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginTop:6 }}>Importez un relevé ou ajoutez une opération manuelle</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width:80 }}>Date</th>
                  <th>Libellé</th>
                  <th style={{ width:130 }}>Catégorie</th>
                  <th style={{ textAlign:'right', width:90 }}>Débit</th>
                  <th style={{ textAlign:'right', width:90 }}>Crédit</th>
                  <th style={{ textAlign:'right', width:105 }}>Solde</th>
                  <th style={{ width:50, textAlign:'center' }}>PJ</th>
                  <th style={{ width:60 }}></th>
                </tr>
              </thead>
              <tbody>
                {withSolde.map(op => {
                  const cfg = TYPE_CONFIG[op.type] || TYPE_CONFIG['E'];
                  return (
                    <tr key={op.id}>
                      <td style={{ fontSize:12, color:'#6b7280' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                      <td>
                        <div style={{ fontSize:12 }}>{op.libelleBrut}</div>
                        {op.note && <div style={{ fontSize:10, color:'#9ca3af' }}>{op.note}</div>}
                        <span style={{ background:cfg.bg, color:cfg.color, borderRadius:999, padding:'1px 6px', fontSize:9, fontWeight:700 }}>{cfg.label}</span>
                      </td>
                      <td style={{ fontSize:11, color:'#6b7280' }}>{op.categorie}</td>
                      <td style={{ textAlign:'right', color:'#991b1b', fontWeight:700, fontSize:12 }}>
                        {op.debit > 0 ? formatMontant(op.debit) : ''}
                      </td>
                      <td style={{ textAlign:'right', color:'#166534', fontWeight:700, fontSize:12 }}>
                        {op.credit > 0 ? formatMontant(op.credit) : ''}
                      </td>
                      <td style={{ textAlign:'right', fontWeight:700, fontSize:12,
                        color: op.soldeApres >= 0 ? '#166534' : '#991b1b' }}>
                        {formatMontant(op.soldeApres)}
                      </td>
                      <td style={{ textAlign:'center' }}>
                        {op.pieceJointe ? (
                          <button className="btn btn-ghost btn-xs" title={op.pieceJointe.nom}
                            onClick={() => window.open(op.pieceJointe.data || op.pieceJointe.url, '_blank')}>
                            📎
                          </button>
                        ) : (
                          <span style={{ color:'#fbbf24', fontSize:13 }} title="Pièce manquante">●</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:3 }}>
                          <button className="btn-icon" style={{ fontSize:12 }} onClick={() => openEdit(op)}>✏️</button>
                          <button className="btn-icon" style={{ fontSize:12, color:'#e57373' }} onClick={() => setConfirmDel(op.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop:'2px solid #e5e7eb', background:'#f9fafb' }}>
                  <td colSpan={3} style={{ fontWeight:700, padding:'8px 10px', fontSize:13 }}>Totaux</td>
                  <td style={{ textAlign:'right', fontWeight:800, color:'#991b1b' }}>{formatMontant(totalDebit)}</td>
                  <td style={{ textAlign:'right', fontWeight:800, color:'#166534' }}>{formatMontant(totalCredit)}</td>
                  <td style={{ textAlign:'right', fontWeight:800, color: (totalCredit-totalDebit) >= 0 ? '#166534' : '#991b1b' }}>
                    {formatMontant(totalCredit - totalDebit)}
                  </td>
                  <td colSpan={2}/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Réconciliation bancaire */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="card-title">⚖️ Réconciliation bancaire</div>
        </div>
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-start', padding:'0 4px' }}>
          {/* Solde ouverture */}
          <div className="stat-card" style={{ flex:1, minWidth:160, borderTopColor:'#6b7280' }}>
            <div className="stat-label">Solde ouverture</div>
            <div className="stat-value" style={{ color:'#6b7280' }}>{formatMontant(moisData.soldeOuverture || 0)}</div>
            {activeMois > 1 && (
              <button className="btn btn-ghost btn-xs" style={{ marginTop:6, fontSize:10 }}
                onClick={saveSoldeOuverture}>↩ Reprendre mois précédent</button>
            )}
          </div>

          {/* Solde calculé */}
          <div className="stat-card" style={{ flex:1, minWidth:160, borderTopColor:'#1d4ed8' }}>
            <div className="stat-label">Solde calculé</div>
            <div className="stat-value" style={{ color:'#1d4ed8' }}>{formatMontant(soldeCalc)}</div>
            <div className="stat-sub">Ouverture + crédits − débits</div>
          </div>

          {/* Solde relevé BFC */}
          <div className="stat-card" style={{ flex:1, minWidth:160, borderTopColor:'#7c3aed' }}>
            <div className="stat-label">Solde relevé BFC</div>
            {editSolde ? (
              <div style={{ display:'flex', gap:6, marginTop:6 }}>
                <input className="form-control" type="number" step="0.01"
                  value={soldeInput} onChange={e => setSoldeInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveSoldeReleve(); if (e.key === 'Escape') setEditSolde(false); }}
                  placeholder="0.00" autoFocus style={{ fontSize:16, fontWeight:700, padding:'4px 8px' }} />
                <button className="btn btn-success btn-sm" onClick={saveSoldeReleve}>✓</button>
                <button className="btn btn-outline btn-sm" onClick={() => setEditSolde(false)}>✕</button>
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginTop:4 }}>
                <span className="stat-value" style={{ color:'#7c3aed' }}>
                  {soldeReleve != null ? formatMontant(soldeReleve) : '—'}
                </span>
                <button className="btn-icon" style={{ fontSize:12 }}
                  onClick={() => { setSoldeInput(String(soldeReleve || '')); setEditSolde(true); }}>✏️</button>
              </div>
            )}
          </div>

          {/* Écart */}
          {ecart != null && (
            <div className="stat-card" style={{ flex:1, minWidth:160, borderTopColor: Math.abs(ecart) < 0.01 ? '#166534' : '#991b1b' }}>
              <div className="stat-label">Écart</div>
              <div className="stat-value" style={{ color: Math.abs(ecart) < 0.01 ? '#166534' : '#991b1b' }}>
                {Math.abs(ecart) < 0.01 ? '✅ 0,00 €' : `⚠️ ${formatMontant(Math.abs(ecart))}`}
              </div>
              <div className="stat-sub">{Math.abs(ecart) < 0.01 ? 'Solde équilibré' : 'Écart à vérifier'}</div>
            </div>
          )}
        </div>
      </div>

      {/* Modal saisie opération */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editId ? '✏️ Modifier' : '+ Nouvelle opération manuelle'}</div>
              <button className="btn-icon" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-control" type="date" value={form.date} onChange={e => f('date')(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select className="form-control" value={form.categorie} onChange={e => f('categorie')(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Libellé *</label>
              <input className="form-control" value={form.libelle} onChange={e => f('libelle')(e.target.value)} placeholder="Description de l'opération" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Débit (€)</label>
                <input className="form-control" type="number" step="0.01" min="0" value={form.debit}
                  onChange={e => { f('debit')(e.target.value); if (e.target.value) f('credit')(''); }} />
              </div>
              <div className="form-group">
                <label className="form-label">Crédit (€)</label>
                <input className="form-control" type="number" step="0.01" min="0" value={form.credit}
                  onChange={e => { f('credit')(e.target.value); if (e.target.value) f('debit')(''); }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Note</label>
              <input className="form-control" value={form.note} onChange={e => f('note')(e.target.value)} placeholder="Optionnel" />
            </div>
            <div className="form-group">
              <label className="form-label">Pièce justificative</label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button className="btn btn-outline btn-sm" onClick={() => docRef.current?.click()}>📎 {pieceJointe ? pieceJointe.nom : 'Ajouter'}</button>
                {pieceJointe && <button className="btn btn-ghost btn-xs" onClick={() => setPieceJointe(null)}>✕</button>}
              </div>
              <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:'none' }} onChange={handleFile} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.date || !form.libelle}>
                {editId ? 'Enregistrer' : '+ Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="modal" style={{ maxWidth:360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Supprimer cette opération ?</div></div>
            <p style={{ fontSize:14, color:'#6b7280' }}>Cette action est irréversible.</p>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDel(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={() => handleDelete(confirmDel)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
